import {Avatar, DatePicker, Input, Radio} from 'antd'
import {getId, getType} from 'apis/model/base'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {LoginContext} from 'components/LoginContext'
import {Formik} from 'formik'
import {getDateString} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useContext, useMemo} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {RiUser3Fill} from 'react-icons/ri'
import {Link} from 'react-router-dom'
import {compose, mapProps} from 'recompose'
import {GenderTypes} from 'views/AppLocals/appFieldTypes'
import {gConfigs} from 'views/AppLocals/configs'
import {appClassNames} from 'views/AppLocals/custom/appClassNames'
import {createControlledFormField} from 'views/AppLocals/factory/createFormField'
import {confirmOnSuccess} from 'views/AppLocals/factory/createRedirectConfirm'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import {renderIf} from '../../../Shared'
import {useEventParticipant} from '../useEventParticipant'
import getLinkToDetail from "../../../../helpers/getLinkToDetail";
import {LayoutContext} from "../../../../components/layouts/Default/LayoutContext";
import {BsCheckCircle, BsPersonFill} from "react-icons/bs";
import dayjs from "dayjs";
import {formatDate} from "../../../../helpers/datetimeHelpers";
import {ItemInformation} from "../../../../components/common/ItemInformation";
import getAvatar from "../../../../helpers/getAvatar";
import getTitle from "../../../../helpers/getTitle";
import {GoDotFill} from "react-icons/go";
import {ChipGroup, Section} from "components/form/ModernForm";


const Header = ({item}) => {
    const t = useTranslate()
    const {
        title,
        time_open,
        time_close,
        event_type,
        location_name,
        external_link,
    } = item ?? {}

    return (
        <div className="flex flex-col">
            <span className="font-semibold text-sm text-color-300 tracking-wide uppercase">
                {t('you are registering to attend the event')}
            </span>
            <div className="flex flex-col gap-1 ml-3">
                <div className="font-bold text-xl text-green-700 italic">
                    {title}
                </div>
                <div className="flex flex-col md:flex-row gap-x-2 text-xs tracking-wide">
                    <span className="leading-tight font-medium text-color-100">
                        <span className="font-normal text-color-500 italic mr-1">
                          {t('time')}{' : '}
                        </span>
                        <span className="font-normal text-color-400 capitalize italic mr-2">
                          {t('from')}
                        </span>
                        {formatDate(time_open, 'hh:mm a - Do MMMM, YYYY')}
                    </span>
                    {renderIf(
                        time_close,
                        <span className="leading-tight font-medium text-color-100">
                            <span className="font-normal text-color-400 lowercase italic mr-2">
                                {t('to')}
                            </span>
                            {formatDate(time_close, 'hh:mm a - Do MMMM, YYYY')}
                        </span>
                    )}
                </div>
                <ItemInformation label="type of event" value={t(event_type)}/>
                <ItemInformation label="address" value={location_name}/>
                <ItemInformation label="link" value={external_link}/>
            </div>
        </div>
    )
}

const UserInfo = ({item}) => {
    const t = useTranslate()
    const {avatar, name, email, username, patron_saint_name} = item ?? {}

    return (
        <div className="mt-1 flex flex-col gap-2">
            <span className="text-color-400 tracking-wider whitespace-nowrap">
                {t('you are registering to attend the event with an account')}:
            </span>
            <div className="p-3 w-full flex items-center gap-3 border border-slate-200 bg-slate-50 rounded-2xl">
                <Avatar
                    size={50}
                    src={avatar}
                    icon={<BsPersonFill size={20} className="text-color-500" />}
                    className="flex flex-center background-200 shadow-md"
                    style={{ border: '2px solid #fff' }}
                />
                <div className="flex flex-col flex-1">
                    <div className="font-bold text-base text-slate-900 tracking-wide max-lines-1">
                        {patron_saint_name && (
                            <span className="mr-1 font-medium text-gray-600">
                                {patron_saint_name}
                            </span>
                        )}
                        {name}
                    </div>
                    <span className="text-xs text-slate-500 leading-snug tracking-wider">
                        {email ? email : username}
                    </span>
                </div>
                <span className="px-1 py-0.5 flex flex-center border border-green-300 bg-green-50 font-medium text-2xs text-green-600 rounded-md">
                    <GoDotFill size={10} className="mr-0.5"/>
                    {t('active')}
                </span>
            </div>
        </div>
    )
}

const formSchema = {
    user: {
        title: 'account',
        children: [
            {
                name: 'user',
                component: mapProps(({value}) => {
                    return {item: value}
                })(UserInfo),
            },
        ],
    },
    name: {
        title: 'full name',
        children: [
            {
                name: 'name',
                component: createControlledFormField({
                    placeholder: 'name',
                }),
            },
        ],
    },
    info_group_1: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'date of birth',
                name: 'date_of_birth',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        className: 'w-full',
                        format: gConfigs.dateFormat,
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: `${translate('date of birth')} "DD/MM/YYYY"`,
                        onChange: (date, dateString) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
            {
                label: 'gender',
                name: 'gender',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                        options: Object.values(GenderTypes)
                    }))
                )(ChipGroup),
            },
        ],
    },
    info_group_2: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'phone',
                name: 'contact_phone',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Input
                                {...{
                                    name,
                                    value,
                                    onChange,
                                }}
                                type="tel"
                                inputMode="tel"
                                placeholder={t('ex: phone')}
                            />
                        )}
                    </Translate>
                ),
            },
            {
                label: 'email',
                name: 'contact_email',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <Input
                                {...{
                                    name,
                                    value,
                                    onChange,
                                }}
                                type="email"
                                inputMode="email"
                                placeholder={t('ex: email')}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
}

const AuthenticatedForm = ({
                               isSubmitting,
                               initialValues,
                               onSubmit = NullAsDefault,
                           }) => {
    const t = useTranslate()

    const validate = (values) => {
        let errors = {}

        const {user} = values ?? {}

        if (!user) {
            errors.user = t('required field')
        }

        return errors
    }

    return (
        <Formik
            onSubmit={(values) => {
                const {user, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            user_id: getId(user),
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}>
            <div className="flex flex-col space-y-3">
                <FieldsFactory formSchema={[formSchema.user]}/>
                <FormActionGroups
                    isSubmitting={isSubmitting}
                    resetProps={{hidden: true}}
                    submitProps={{label: 'register', dirtyRequired: false}}
                />
            </div>
        </Formik>
    )
}

const UnauthenticatedForm = ({
                                 isSubmitting,
                                 initialValues,
                                 onSubmit = NullAsDefault,
                             }) => {
    const t = useTranslate()

    const _initialValues = useMemo(
        () => ({
            gender: undefined,
            name: undefined,
            contact_phone: undefined,
            contact_email: undefined,
            date_of_birth: undefined,
            ...(initialValues ?? {}),
        }),
        [initialValues]
    )

    const validate = (values) => {
        let errors = {}

        const {...rest} = values ?? {}

        const name = String(values?.name)

        if (_.isEmpty(name)) {
            errors.name = t('required field')
        }

        return errors
    }

    return (
        <Formik
            onSubmit={(values) => {
                const {gender, date_of_birth, ...rest} = values ?? {}
                onSubmit(
                    _.omitBy(
                        {
                            ...rest,
                            date_of_birth: getDateString(date_of_birth),
                            gender: gender ? Number(gender) : undefined,
                        },
                        _.isUndefined
                    )
                )
            }}
            validate={validate}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={_initialValues}>
            <div className="flex flex-col gap-4">
                <Section title={t('basic information')}>
                    <FieldsFactory
                        formSchema={[
                            formSchema.name,
                            formSchema.info_group_1,
                        ]}
                    />
                </Section>
                <Section title={t('contact information')}>
                    <FieldsFactory formSchema={[formSchema.info_group_2]}/>
                </Section>
                <div className="space-x-1 tracking-wider">
                    <span className="font-semibold text-gray-600">
                        {`* ${t('note')}: `}
                    </span>
                    <span className="text-color-200 italic">
                        {t('note register description')}.
                    </span>
                </div>
                <FormActionGroups
                    isSubmitting={isSubmitting}
                    resetProps={{hidden: true}}
                    submitProps={{label: 'register'}}
                />
            </div>
        </Formik>
    )
}

const EventSubscribeForm = ({item, onCancel = NullAsDefault, ...props}) => {
    const t = useTranslate()
    const {isSm} = useContext(LayoutContext)

    const login = useContext(LoginContext)

    const owner_id = getId(item)

    const owner_type = getType(item)

    const {subscribeAsync, onSubscribe = NullAsDefault} = useEventParticipant({
        onSubscribed: (result, data) => {
            onCancel()
            const item = getResponseItem(data?.response)
            confirmOnSuccess({
                translate: t,
                Icon: BsCheckCircle,
                title: 'registration successful',
                description: 'registration successful content',
            })
        },
    })

    const handleSubmit = (values) => {
        onSubscribe(values, {id: owner_id, type: owner_type})
    }

    const Component = login ? AuthenticatedForm : UnauthenticatedForm

    return (
        <div className="flex flex-col gap-6">
            {renderIf(
                isSm,
                <Header item={item}/>
            )}
            <Component
                {...props}
                onSubmit={handleSubmit}
                isSubmitting={subscribeAsync?.isLoading}
                initialValues={{
                    user: login,
                }}
            />
        </div>
    )
}

export default EventSubscribeForm
